import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { MoreHoriz } from '@mui/icons-material';

import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import type { PortalUserAuditLogEntry } from '../../../store/modules/merchant/types';
import { PortalUserAuditActionChip } from './PortalUserAuditActionChip';
import { PortalUserAuditChanges, PortalUserAuditSnapshotSummary } from './PortalUserAuditChanges';
import { getPortalUserAuditTargetName } from '../portalUserAuditNames';

type Props = {
  auditLog: PortalUserAuditLogEntry;
};

const DetailField = ({
  label,
  children,
  md = 12,
}: {
  label: string;
  children: React.ReactNode;
  md?: number;
}) => (
  <Grid item xs={12} md={md}>
    <Typography variant="small" fontWeight="semibold" color="grey.600">
      {label}
    </Typography>
    {typeof children === 'string' || typeof children === 'number' ? (
      <Typography variant="medium" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </Grid>
);

const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="large" fontWeight="semibold" color="text.primary" mb={1.5}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

export const PortalUserAuditLogDetails = ({ auditLog }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        aria-label="View portal user audit log details"
        sx={{ m: -1.5, color: 'link.main' }}
      >
        <MoreHoriz />
      </IconButton>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          User audit log details
          <DialogCloseButton onClose={() => setIsOpen(false)} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <DetailSection title="Event">
              <DetailField label="Action">
                <Box mt={0.5}>
                  <PortalUserAuditActionChip
                    action={auditLog.action}
                    emailType={auditLog.metadata?.emailType}
                    size="small"
                  />
                </Box>
              </DetailField>
              <DetailField label="Timestamp">
                {dayjs(auditLog.timestamp).format('MM/DD/YY hh:mm:ss A')}
              </DetailField>
            </DetailSection>

            <DetailSection title="Performed by">
              <DetailField label="Email">{auditLog.actorEmail || '—'}</DetailField>
            </DetailSection>

            <DetailSection title="Affected user">
              <DetailField label="Name" md={6}>
                {getPortalUserAuditTargetName(auditLog) || '—'}
              </DetailField>
              <DetailField label="Email" md={6}>
                {auditLog.targetUserEmail || '—'}
              </DetailField>
            </DetailSection>

            {auditLog.action === 'update' && (
              <PortalUserAuditChanges changes={auditLog.changes ?? []} />
            )}
            {auditLog.action === 'create' && (
              <PortalUserAuditSnapshotSummary title="Created user" snapshot={auditLog.newValue} />
            )}
            {auditLog.action === 'delete' && (
              <PortalUserAuditSnapshotSummary
                title="Deleted user"
                snapshot={auditLog.previousValue}
              />
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
